/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { RestaurantCustomerAddress as CustomerAddress } from "@lemmonhq/types";
import { AddressDisplay } from "./AddressDisplay";
import { getAPIClient } from "@app/data/helpers";

export interface CustomerAddressListProps {
  hidden?: boolean;
  addresses: CustomerAddress[];
  selectedAddressId: string | undefined;
  onSelect: (id: string) => void;
  onAddNew: () => void;
  isLoading: boolean;
}

export const CustomerAddressList = defineComponent<CustomerAddressListProps>({
  name: "CustomerAddressList",
  props: [
    "hidden",
    "addresses",
    "selectedAddressId",
    "onSelect",
    "onAddNew",
    "isLoading",
  ],
  setup(props) {
    const selectedAddress = computed(() =>
      props.addresses.find((address) => address.id === props.selectedAddressId),
    );

    const canPreview = computed(() => {
      const current = selectedAddress.value;
      return (
        !!current?.latitude &&
        !!current?.longitude &&
        !Number.isNaN(Number(current.latitude)) &&
        !Number.isNaN(Number(current.longitude))
      );
    });

    const staticMapUrl = computed(() => {
      if (!canPreview.value) return null;
      const current = selectedAddress.value!;
      if (!current) return null;
      const hc = getAPIClient();
      console.log(current);
      const url = `/api/v2/geolocation/static-map?lat=${current.latitude}&lon=${current.longitude}&label=${current.street}`;

      console.log(url);
      return url.toString();
    });

    return () => {
      if (props.hidden) {
        return null;
      }

      return (
        <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div class="space-y-4">
            {props.isLoading ? (
              <div class="flex items-center justify-center py-6">
                <span class="loading loading-spinner loading-md text-primary" />
              </div>
            ) : props.addresses.length === 0 ? (
              <div class="rounded-2xl border border-dashed border-base-300 p-6 text-center space-y-3">
                <span class="material-symbols-outlined text-4xl text-base-content/40">
                  map
                </span>
                <h4 class="font-semibold text-base-content">
                  No saved addresses yet
                </h4>
                <p class="text-sm text-base-content/70 max-w-sm mx-auto">
                  Capture an address to remember this customer's delivery
                  details for the next order.
                </p>
                <button class="btn btn-primary" onClick={props.onAddNew}>
                  Add delivery address
                </button>
              </div>
            ) : (
              <>
                <div class="space-y-3">
                  {props.addresses.map((address) => (
                    <button
                      key={address.id}
                      class={`card text-left border transition-all w-full ${
                        props.selectedAddressId === address.id
                          ? "border-primary shadow-lg bg-primary/5"
                          : "border-base-300 hover:border-primary/60 hover:shadow"
                      }`}
                      onClick={() => props.onSelect(address.id)}
                    >
                      <div class="card-body py-2 px-4 gap-2">
                        <div class="flex items-center justify-between gap-2">
                          <h4 class="font-semibold truncate">
                            {address.name ||
                              `${address.street}, ${address.city}`}
                          </h4>
                          {props.selectedAddressId === address.id && (
                            <span class="badge badge-primary badge-outline flex-shrink-0">
                              Selected
                            </span>
                          )}
                        </div>
                        <AddressDisplay
                          street={address.street}
                          city={address.city}
                          postalCode={address.postal_code}
                          companyName={address.company_name || undefined}
                          phone={address.phone}
                          email={address.email}
                          variant="compact"
                        />
                      </div>
                    </button>
                  ))}
                </div>
                <div class="flex justify-start hidden">
                  <button
                    class="btn btn-outline btn-sm"
                    onClick={props.onAddNew}
                  >
                    <span class="material-symbols-outlined">add</span>
                    New address
                  </button>
                </div>
              </>
            )}
          </div>
          <div class="rounded-2xl border border-base-300 bg-base-100 shadow-inner min-h-[220px] flex flex-col items-stretch justify-start p-6 space-y-4">
            {canPreview.value && staticMapUrl.value ? (
              <>
                <img
                  src={staticMapUrl.value}
                  alt={`Map preview for ${
                    selectedAddress.value?.street ?? "selected address"
                  }`}
                  class="w-full h-44 rounded-xl object-cover shadow"
                />
                <div class="space-y-3 text-left">
                  <h4 class="font-semibold text-base-content">
                    {selectedAddress.value?.name || "Address Details"}
                  </h4>
                  <AddressDisplay
                    street={selectedAddress.value?.street}
                    city={selectedAddress.value?.city}
                    postalCode={selectedAddress.value?.postal_code}
                    companyName={
                      selectedAddress.value?.company_name || undefined
                    }
                    phone={selectedAddress.value?.phone}
                    email={selectedAddress.value?.email}
                    variant="detailed"
                  />
                  <div class="pt-2 border-t border-base-300">
                    <p class="flex items-center gap-1.5 text-xs text-base-content/60">
                      <span class="material-symbols-outlined text-sm">
                        location_on
                      </span>
                      {Number(selectedAddress.value?.latitude).toFixed(5)},{" "}
                      {Number(selectedAddress.value?.longitude).toFixed(5)}
                    </p>
                  </div>
                  <p class="text-xs text-base-content/40 text-center pt-2">
                    Map powered by Mapbox
                  </p>
                </div>
              </>
            ) : (
              <div class="flex flex-col items-center justify-center text-center flex-1">
                <div class="rounded-full bg-primary/10 text-primary p-3">
                  <span class="material-symbols-outlined text-3xl">
                    pin_drop
                  </span>
                </div>
                <h4 class="font-semibold text-base-content mt-4">
                  Map preview
                </h4>
                <p class="text-sm text-base-content/70 max-w-xs mt-2">
                  Select an address with location data to preview it on the map.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    };
  },
});
